export class AnalyticsEvents {
    // Define your event names here to ensure consistency



    /// Triggered when the Auth/Onboarding Modal is viewed.
    static readonly AuthModalViewed = 'AuthModal Viewed';

    /// Triggered when the main app UI initializes.
    static readonly AppOpened = 'App Opened';

    /// Triggered when a user views a specific page.
    static readonly PageViewed = 'Page Viewed';

    // --- Auth Events ---

    /// Triggered whenever an OTP is sent (via login or signup).
    /// Properties: `country_code`, `method`, `phone`.
    static readonly OtpSent = 'OTP Sent';

    /// Triggered when the API call to send an OTP fails.
    /// Properties: `country_code`, `method`, `phone`, `error`.
    static readonly OtpSendFailed = 'OTP Send Failed';

    /// Triggered when the user requests an OTP resend.
    /// Properties: `country_code`, `method`, `phone`.
    static readonly OtpResent = 'OTP Resent';

    /// Triggered when the OTP resend request fails.
    /// Properties: `country_code`, `method`, `phone`, `error`.
    static readonly OtpResentFailed = 'OTP Resend Failed';

    /// Triggered upon successful OTP verification and login.
    /// Properties: `country_code`, `method`, `phone`.
    static readonly LoginSuccess = 'Login Success';

    /// Triggered when OTP verification fails.
    /// Properties: `country_code`, `method`, `phone`, `error`.
    static readonly LoginFailed = 'Login Failed';

    /// Triggered when the user explicitly logs out.
    static readonly UserLoggedOut = 'User Logged Out';

    // --- Page View Events ---

    /// Triggered when Home Screen (Landing Page) is initialized.
    static readonly PageViewedHome = 'Page Viewed: Home';

    /// Triggered when the Profile Setup screen is visited.
    static readonly PageViewedCreateProfile = 'Page Viewed: Create Profile';

    /// Triggered when the Wallet/Recharge screen is visited.
    static readonly PageViewedWallet = 'Page Viewed: /wallet';

    /// Triggered when the Chat screen is visited.
    static readonly PageViewedChat = 'Page Viewed: Chat';

    /// Triggered when the Reports tab is visited.
    static readonly ReportsTabVisited = 'Reports Tab Visited';

    /// Triggered when the Charts tab is visited.
    static readonly ChartsTabVisited = 'charts tab visited(app)';

    // --- Profile Events ---

    /// Triggered when a new profile is submitted.
    static readonly ProfileCreationSubmitted = 'Profile Creation Submitted';

    // --- Chat Events ---

    /// Triggered when a user sends a chat message.
    /// Properties: `category`, `sub_category`, `profile_name`, `age`, `gender`.
    static readonly ChatMessageSent = 'Chat Message Sent';

    /// Activation metric: fires ONCE, the first time a user ever asks a
    /// question ("your first question is free" is the core promise).
    /// Lifetime-accurate, not per-session — see utils/firstQuestionTracker.ts.
    /// Properties: `profile_id`, `source`, `question_category`.
    static readonly FirstQuestionSubmitted = 'first_question_submitted';

    /// (Unused/Backend) Triggered when a message is received.
    static readonly ChatMessageReceived = 'Chat Message Received';

    /// Triggered when a user clicks a suggested follow-up question.
    /// Properties: `category`.
    static readonly AiGeneratedQuestionClick = 'AI Generated Question Click';

    // --- Call Events ---

    /// Triggered when the call button on Home is clicked.
    static readonly CallButtonClicked = 'Call Button Clicked';

    /// Triggered when the AI Call ends.
    static readonly AiCallEnded = 'AI Call Ended';

    /// (Alternate) Voice Call Ended.
    static readonly VoiceCallEnded = 'Voice Call Ended';

    // --- Report Events ---

    /// Triggered when unlocking a report is attempted.
    static readonly AiReportUnlockStarted = 'AI Report Unlock Started';

    /// Triggered when report generation/payment is confirmed.
    static readonly AiReportGenerationConfirmed = 'AI Report Generation Confirmed';

    /// Triggered when a report preview is viewed from the Chat Screen.
    static readonly ReportPreviewChatScreen = 'ReportPreviewChatScreen';

    /// Triggered when a report preview is viewed from the Reports Screen.
    static readonly ReportPreviewReportScreen = 'ReportPreviewReportScreen';


    // --- Payment/Wallet Events ---

    /// Triggered when the Recharge flow starts.
    /// Properties: `amount_pay`, `amount_credit`, `currency`.
    static readonly QuickRechargeStarted = 'Quick Recharge Started';

    /// Triggered if payment fails.
    /// Properties: `reason`, `error_code`.
    static readonly QuickRechargeFailed = 'Quick Recharge Failed';

    /// Triggered on successful payment response.
    static readonly QuickRechargeSucceeded = 'Quick Recharge Succeeded';

    /// Triggered when the GST Breakdown dialog is viewed.
    /// Properties: `base_amount`, `gst_amount`, `total_payable`, `currency`.
    static readonly GstSummaryViewed = 'gst_summary_viewed';

    /// Triggered when the Chat Recharge Modal is viewed.
    static readonly MonetizationModalViewed = 'Monetization Modal Viewed';

    /// Triggered when Today's Horoscope is generated via CTA.
    static readonly TodaysHoroscopeGenerated = 'todays horoscope generated';

    /// Triggered when Yesterday's Horoscope is generated via CTA.
    static readonly YesterdaysHoroscopeGenerated = 'yesterdays horoscope generated';

    /// Triggered when Tomorrow's Horoscope is generated via CTA.
    static readonly TomorrowsHoroscopeGenerated = 'tomorrows horoscope generated';

    /// Triggered when user clicks a date tab (Yesterday/Today/Tomorrow).
    /// Properties: `date`.
    static readonly HoroscopeDateTabClicked = 'Horoscope Date Tab Clicked';

    /// Triggered when user clicks "Read Full Horoscope".
    /// Properties: `date`.
    static readonly HoroscopeReadFullClicked = 'Horoscope Read Full Clicked';

    /// Triggered when the full Horoscope Screen is viewed.
    /// Properties: `date`.
    static readonly HoroscopeFullScreenViewed = 'Horoscope Full Screen Viewed';

    /// Triggered when user clicks a suggested question on Horoscope Card.
    /// Properties: `question`.
    static readonly HoroscopeSuggestedQuestionClicked = 'horoscope_suggested_question_click';

    // --- Guest Chat Funnel (website, non-Indian visitors only) ---
    // Logged-out foreign visitors can open /chat, ask one question and see a
    // blurred (dummy) answer before signing in. These four events are the funnel;
    // they are NEW names — no existing event was renamed.

    /// Guest opened the teaser chat screen.
    static readonly GuestChatViewed = 'Guest Chat Viewed';

    /// Guest sent their one free question (no API call — blurred answer shown).
    /// Properties: `message_length`, `source`.
    static readonly GuestQuestionAsked = 'Guest Question Asked';

    /// Guest tapped "View for free" on the blurred answer (opens the login sheet).
    /// Properties: `source`.
    static readonly GuestUnlockClicked = 'Guest Unlock Clicked';

    /// The parked guest question was sent FOR REAL after sign-in + profile
    /// creation — i.e. the funnel converted. Properties: `message_length`.
    static readonly GuestQuestionAnswered = 'Guest Question Answered';

    //Web specific but consistent naming style
    static readonly GoogleLoginStarted = 'Google Login Started';
    static readonly PhoneLoginStarted = 'Phone Login Started';

    // --- Compatibility calculator funnel (/kundli-matching) ---
    // Named as a funnel on purpose: campaigns are optimised against the step
    // that predicts a signup, so each step has to be separable from the next.

    /// Both sets of birth details are saved and the match CTA became available.
    static readonly CompatCalcReady = 'Compat Calc Ready';

    /// The visitor tapped the CTA and hit the phone gate.
    static readonly CompatCalcGateShown = 'Compat Calc Gate Shown';

    /// Phone verified from the compatibility gate. This is the lead.
    static readonly CompatCalcLead = 'Compat Calc Lead';

    /// The gun milan score was actually rendered.
    static readonly CompatCalcScoreViewed = 'Compat Calc Score Viewed';

    /// Tapped through from the score into the compatibility chat.
    static readonly CompatCalcChatClicked = 'Compat Calc Chat Clicked';
}
